/**
 * Render resolution control (section 5.2: "Allow 720p/1080p/native render
 * resolution").
 *
 * Dropping render resolution is the single biggest performance lever on a large
 * panel: a 4K IFP has four times the pixels of a 1080p one, and fragment-heavy
 * work (shadows, particles, post-processing) scales directly with that. Being
 * able to flip resolution while a scene runs is what turns prototype 4 into a
 * real measurement instead of a guess.
 */

export const RESOLUTION_MODES = ['720p', '1080p', 'native'] as const;
export type ResolutionMode = (typeof RESOLUTION_MODES)[number];

export const DEFAULT_RESOLUTION: ResolutionMode = 'native';

/**
 * Convert a target mode into a pixel ratio for the given CSS height.
 *
 * PlayCanvas multiplies the canvas CSS size by `maxPixelRatio` to get the
 * backing buffer, and internally clamps that to `window.devicePixelRatio` - so
 * this can only ever reduce resolution, never supersample.
 */
export function pixelRatioFor(mode: ResolutionMode, clientHeight: number): number {
  const dpr = window.devicePixelRatio || 1;
  if (mode === 'native') return dpr;

  const targetHeight = mode === '720p' ? 720 : 1080;
  const height = Math.max(clientHeight, 1);
  return Math.min(dpr, targetHeight / height);
}

export function isResolutionMode(value: string): value is ResolutionMode {
  return (RESOLUTION_MODES as readonly string[]).includes(value);
}
